import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  PAGE_LIMIT_MAX,
  formatDateOnly,
  formatTaskDue,
  toDateInput,
  errorWords,
} from "@alm/domain";
import { relationshipLabel, statusLabel } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { orpc } from "@/lib/orpc";
import { useCan, useCanWithoutBuilding, usePolicy } from "@/lib/policy";
import { TaskPeopleCard } from "./-components/task-people-card";
import { TaskActivity } from "./-components/task-activity";
import { TaskForm } from "./-components/task-form";
import { TaskStatusMenu } from "./-components/task-status-menu";
import { EMPTY_TASK, linksOf, taskToInput, type TaskValues } from "./-components/task-values";
import { Field } from "./-components/task-facts";
import { TaskLinks } from "@/routes/_authenticated/_support/task-center/-components/task-links";
import { useTaskChoices } from "./-components/use-task-choices";

export const Route = createFileRoute("/_authenticated/_support/task-center/$taskId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.support.tasks.get.queryOptions({ input: { taskId: params.taskId } }),
    ),
  component: TaskDetailPage,
});

const fail = (error: Error) => toast.error(errorWords(error));
function TaskDetailPage() {
  const { taskId } = Route.useParams();
  const { subject } = usePolicy();
  // Deleting somebody else's task is checked by the service with no building named.
  const canAdminister = useCanWithoutBuilding("organization:administer");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { leads } = useTaskChoices(editing);
  const [assignee, setAssignee] = useState("");
  const { data: task } = useSuspenseQuery(
    orpc.support.tasks.get.queryOptions({ input: { taskId } }),
  );
  // The task's own building answers, as the API reads it off the task.
  const canManage = useCan("task:manage", task.primaryFacilityId ?? null);
  const residents = useQuery(
    orpc.care.residents.list.queryOptions({
      input: { status: "active" as const, limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const people = useQuery(
    orpc.administration.staff.list.queryOptions({
      input: { includeInactive: false, limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
  );
  const done = (message: string) => async () => {
    toast.success(message);
    setEditing(false);
    setAssignee("");
    await queryClient.invalidateQueries({ queryKey: orpc.support.tasks.key() });
  };
  /* Editing what a task is about is its own pair of writes, because `update`
     does not carry links: the old one comes off and the new one goes on. */
  const addLink = useMutation(
    orpc.support.tasks.links.add.mutationOptions({
      onSuccess: done("Task updated"),
      onError: fail,
    }),
  );
  const removeLink = useMutation(
    orpc.support.tasks.links.remove.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.support.tasks.key() }),
      onError: fail,
    }),
  );
  const m = {
    update: useMutation(
      orpc.support.tasks.update.mutationOptions({ onSuccess: done("Task updated"), onError: fail }),
    ),
    complete: useMutation(
      orpc.support.tasks.complete.mutationOptions({
        onSuccess: done("Task completed"),
        onError: fail,
      }),
    ),
    reopen: useMutation(
      orpc.support.tasks.reopen.mutationOptions({
        onSuccess: done("Task reopened"),
        onError: fail,
      }),
    ),
    remove: useMutation(
      orpc.support.tasks.remove.mutationOptions({
        onSuccess: async () => {
          toast.success("Task deleted");
          await navigate({ to: "/task-center" });
          await queryClient.invalidateQueries({ queryKey: orpc.support.tasks.key() });
        },
        onError: fail,
      }),
    ),
    setAssignee: useMutation(
      orpc.support.tasks.assignees.set.mutationOptions({
        onSuccess: done("Assignment saved"),
        onError: fail,
      }),
    ),
    removeAssignee: useMutation(
      orpc.support.tasks.assignees.remove.mutationOptions({
        onSuccess: done("Assignment removed"),
        onError: fail,
      }),
    ),
    respond: useMutation(
      orpc.support.tasks.assignees.respond.mutationOptions({
        onSuccess: done("Answer recorded"),
        onError: fail,
      }),
    ),
  };
  const pending = Object.values(m).some((mutation) => mutation.isPending);
  const closed = task.status === "completed" || task.status === "cancelled";
  const myAssignment = task.assignments.find((row) => row.userId === subject.userId);
  const unassigned = (people.data?.rows ?? []).filter(
    (person) => !task.assignments.some((row) => row.userId === person.id),
  );
  const due = formatTaskDue(task.dueDate, task.dueTime, formatDateOnly);

  const about = task.links.find(
    (link) => link.entityType === "resident" || link.entityType === "lead",
  );
  const defaults: TaskValues = {
    ...EMPTY_TASK,
    title: task.title,
    description: task.description ?? "",
    status: task.status as TaskValues["status"],
    priority: task.priority as TaskValues["priority"],
    category: task.category as TaskValues["category"],
    scope: task.scope as TaskValues["scope"],
    primaryFacilityId: task.primaryFacilityId ?? EMPTY_TASK.primaryFacilityId,
    dueDate: toDateInput(task.dueDate),
    dueTime: task.dueTime ?? "",
    aboutType: about ? (about.entityType as TaskValues["aboutType"]) : "none",
    aboutId: about?.entityId ?? EMPTY_TASK.aboutId,
    tags: task.tags ?? [],
  };

  return (
    <>
      <PageHeader
        /* The badges sit under the title, not in it: inside the h1 a screen
           reader heard "…temperature logOpenHigh" as the page's name. */
        title={task.title}
        meta={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge variant={task.status === "completed" ? "ok" : "secondary"}>
              {statusLabel(task.status)}
            </Badge>
            <Badge variant="outline">{relationshipLabel(task.priority)} priority</Badge>
            <span>
              <Link to="/task-center" className="hover:underline">
                Tasks
              </Link>
              {`, ${relationshipLabel(task.category)}`}
              {due ? `, due ${due}` : ""}
            </span>
          </span>
        }
        actions={
          canManage ? (
            <span className="flex flex-wrap gap-2">
              <TaskStatusMenu
                disabled={pending}
                onChange={(status) => m.update.mutate({ taskId, status })}
                status={task.status}
                title={task.title}
              />
              <Button variant="outline" onClick={() => setEditing(true)}>
                Edit task
              </Button>
              {closed ? (
                <Button disabled={pending} onClick={() => m.reopen.mutate({ taskId })}>
                  Reopen task
                </Button>
              ) : (
                <Button disabled={pending} onClick={() => m.complete.mutate({ taskId })}>
                  Complete task
                </Button>
              )}
            </span>
          ) : null
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Detail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm whitespace-pre-wrap">
              {task.description || <span className="text-muted-foreground">No description.</span>}
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Scope">{relationshipLabel(task.scope)}</Field>
              <Field label="Facilities">
                {task.facilities.length
                  ? task.facilities.map((facility) => facility.name).join(", ")
                  : "-"}
              </Field>
              <Field label="Completed">
                {task.completedAt ? formatDateOnly(task.completedAt) : "-"}
              </Field>
            </div>
            {task.links.length ? <TaskLinks links={task.links} /> : null}
          </CardContent>
        </Card>
        <TaskPeopleCard
          assignments={task.assignments}
          myAssignment={myAssignment}
          unassigned={unassigned}
          canManage={canManage}
          pending={pending}
          assignee={assignee}
          onAssignee={setAssignee}
          onRemove={(userId) => m.removeAssignee.mutate({ taskId, userId })}
          onRespond={(acceptanceStatus) => m.respond.mutate({ taskId, acceptanceStatus })}
          onAssign={() => m.setAssignee.mutate({ taskId, userId: assignee, role: "assignee" })}
        />
      </div>
      <TaskActivity taskId={taskId} facilityId={task.primaryFacilityId ?? null} />
      {/* The server holds the same rule: the creator or an administrator. */}
      {canManage && (task.createdBy === subject.userId || canAdminister) ? (
        <div className="mt-4">
          <Button
            variant="ghost"
            className="text-destructive"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete task
          </Button>
        </div>
      ) : null}
      {confirmingDelete ? (
        <RemoveConfirm
          title={`Delete "${task.title}"?`}
          description="It is removed from every list and cannot be undone."
          confirmLabel="Delete task"
          onConfirm={() => m.remove.mutate({ taskId })}
          onClose={() => setConfirmingDelete(false)}
        />
      ) : null}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit task</DialogTitle>
          </DialogHeader>
          <TaskForm
            mode="edit"
            defaultValues={defaults}
            facilities={facilities.data ?? []}
            residents={(residents.data?.rows ?? []).map((resident) => ({
              id: resident.id,
              name: `${resident.firstName} ${resident.lastName}`,
            }))}
            leads={leads}
            pending={pending}
            onCancel={() => setEditing(false)}
            onSubmit={(values) => {
              m.update.mutate({ taskId, ...taskToInput(values) });
              const now = linksOf(values)[0];
              if (about?.entityId === now?.entityId) return;
              if (about) removeLink.mutate({ taskId, linkId: about.id });
              if (now) addLink.mutate({ taskId, ...now });
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
